<template>
  <div>
    <!-- Filters -->
    <FilterCard :applied-filter-count="appliedFilterCount">
      <b-row>
        <b-col>
          <b-row class="align-items-center">
            <b-col
              cols="12"
              md="4"
              xl="3"
              class="mb-1 mb-xl-0"
            >
              <b-form-group
                label="Bag ID"
                label-for="bag-id"
                class="mb-0"
              >
                <b-form-input
                  id="bag-id"
                  v-model="filters.bag_id"
                  placeholder="Bag ID"
                />
              </b-form-group>
            </b-col>
            <b-col
              cols="12"
              md="4"
              xl="3"
              class="mb-1 mb-xl-0"
            >
              <b-form-group
                label="Merchant"
                label-for="merchant"
                class="mb-0"
              >
                <v-select
                  id="merchant"
                  v-model="filters.merchant_id"
                  :options="merchantOptions"
                  :reduce="merchant => merchant.value"
                  label="text"
                  placeholder="Select Merchant"
                  @input="handleMerchantChange"
                />
              </b-form-group>
            </b-col>
            <b-col
              cols="12"
              md="4"
              xl="3"
              class="mb-1 mb-xl-0"
            >
              <b-form-group
                label="Merchant Business"
                label-for="merchant-business"
                class="mb-0"
              >
                <v-select
                  id="merchant-business"
                  v-model="filters.merchant_business_id"
                  :options="merchantBusinessOptions"
                  :reduce="business => business.id"
                  label="business_name"
                  placeholder="Select Merchant Business"
                />
              </b-form-group>
            </b-col>
            <b-col
              cols="12"
              md="4"
              xl="3"
              class="mb-1 mb-xl-0"
            >
              <b-form-group
                label="Created At"
                label-for="date"
                class="mb-0"
              >
                <AdvancedDateFilter
                  :date-value="filters.date"
                  :opens="'left'"
                  @update="updateDate"
                />
              </b-form-group>
            </b-col>
          </b-row>
          <!-- Apply and Clear Filters -->
          <b-row class="mt-1">
            <b-col class="d-flex justify-content-end">
              <filter-buttons
                :filter-data="filters"
                :apply-filters="applyFilters"
                :clear-filters="clearFilters"
              />
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </FilterCard>

    <b-card no-body>
      <!-- Action Row -->
      <div class="m-2">
        <b-row>
          <b-col
            class="d-flex align-items-center justify-content-end mb-1 mb-md-0"
          >
            <ReportTableButtons
              :items="reportData"
              :json_fieldz="json_fields"
              :name="`ReturnBagReporting - Page ${page}.xls`"
              :bulkname="`ReturnBagReporting.xls`"
              :fetch="getReturnBagReportNoPagination"
              @refresh="fetchData"
            />
          </b-col>
        </b-row>
      </div>

      <!-- Table -->
      <b-overlay
        id="overlay-background"
        blur="10px"
        opacity="0.30"
        rounded="sm"
        variant="light"
      >
        <b-table
          :items="reportData"
          :fields="fields"
          striped
          hover
          responsive
          show-empty
          class="mb-0 hide-vertical-scroll"
        >
          <template #empty>
            <TableDataFetching
              :rows="reportData"
              :data-loading="loading"
            />
          </template>

          <template #head()="{ field }">
            <div class="sortable-header">
              <span class="header-text">{{ field.label }}</span>
              <feather-icon
                v-if="field.sortable"
                :icon="sort === field.key
                  ? 'ArrowDownIcon'
                  : 'ArrowUpIcon'"
                size="14"
              />
            </div>
          </template>

          <template #cell(bag_id)="data">
            <div class="d-flex align-items-center">
              <b-avatar
                :text="avatarText(data.item.merchant_name || data.value)"
                size="32"
                variant="light-primary"
              />
              <div>
                <div class="font-weight-bolder ml-1 text-primary">
                  {{ data.value }}
                </div>
              </div>
            </div>
          </template>

          <template #head(no_of_orders)="{ field }">
            <div class="sortable-header justify-content-end">
              <span class="header-text">{{ field.label }}</span>
              <feather-icon
                v-if="field.sortable"
                :icon="sort === field.key
                  ? 'ArrowDownIcon'
                  : 'ArrowUpIcon'"
                size="14"
              />
            </div>
          </template>
          <template #cell(date)="data">
            <div>
              {{ data.value ? __dateTimeFormatter(data.value) : '---' }}
            </div>
          </template>
          <template #cell(is_closed)="data">
            <div class="d-flex align-items-center">
              <span
                class="bag-status-dot"
                :class="data.item.is_closed ? 'bag-status-dot--closed' : 'bag-status-dot--open'"
              />
              <span>{{ data.item.is_closed ? 'Closed' : 'Open' }}</span>
            </div>
          </template>
          <template #cell(no_of_orders)="data">
            <span>{{ __numberWithCommas(data.value) }}</span>
          </template>

          <template #cell(actions)="data">
            <b-dropdown
              variant="link"
              no-caret
              class="p-0"
              right
            >
              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="align-middle text-body"
                />
              </template>
              <b-dropdown-item @click="viewOrders(data.item)">
                <feather-icon
                  icon="EyeIcon"
                  class="mr-50"
                />
                <span class="align-middle">View Orders</span>
              </b-dropdown-item>
              <b-dropdown-item @click="downloadCsv(data.item.id, data.item.custom_id)">
                <feather-icon
                  icon="DownloadIcon"
                  class="mr-50"
                />
                <span class="align-middle">Download</span>
              </b-dropdown-item>
              <!-- PDF Option -->
              <b-dropdown-item @click="downloadPdf(data.item)">
                <feather-icon
                  icon="FileTextIcon"
                  class="mr-50"
                />
                <span class="align-middle">PDF</span>
              </b-dropdown-item>
            </b-dropdown>
          </template>

        </b-table>

        <!-- Pagination -->
        <div class="mx-2 mb-2 mt-2">
          <b-row>
            <b-col
              cols="12"
              md="3"
              class="d-flex align-items-center justify-content-sm-start"
            >
              <label>Per page</label>
              <v-select
                v-model="perPage"
                :options="pageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block ml-1 width-125 mr-1"
              />
            </b-col>
            <b-col
              cols="12"
              md="9"
              class="d-flex align-items-center justify-content-sm-end"
            >
              <span class="text-muted pagination-text mt-1 mt-md-0 mr-1">
                Showing {{ meta.from || 0 }} to {{ meta.to || 0 }} of {{ meta.total || 0 }} entries
              </span>
              <b-pagination
                v-model="page"
                :total-rows="meta.total || 0"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
              >
                <template #prev-text>
                  <feather-icon
                    icon="ChevronLeftIcon"
                    size="18"
                  />
                </template>
                <template #next-text>
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="18"
                  />
                </template>
              </b-pagination>
            </b-col>
          </b-row>
        </div>
      </b-overlay>
    </b-card>
  </div>
</template>

<script>
import {
  BRow, BCol, BCard, BFormGroup, BFormInput, BTable, BDropdown, BDropdownItem, BPagination, BOverlay, BAvatar,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import FilterCard from '@/components/FilterCard/FilterCard.vue'
import FilterButtons from '@/components/Filter/FilterButtons.vue'
import AdvancedDateFilter from '@/components/AdvancedDateFilter/AdvancedDateFilter.vue'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import moment from 'moment'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import { avatarText } from '@core/utils/filter'
import JsPDF from 'jspdf'
import 'jspdf-autotable'
import QRCode from 'qrcode'
// eslint-disable-next-line import/named
import { barcode } from '@core/utils/utils'

const sortingRepository = RepositoryFactory.get('sorting')
const merchantRepository = RepositoryFactory.get('merchant')

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BFormGroup,
    BFormInput,
    BTable,
    BDropdown,
    BDropdownItem,
    BPagination,
    BOverlay,
    BAvatar,
    vSelect,
    FilterCard,
    FilterButtons,
    AdvancedDateFilter,
  },
  mixins: [SuccessMessage, ErrorMessage],
  data() {
    return {
      loading: false,
      merchantOptions: [],
      merchantBusinessOptions: [],
      sort: '',
      filters: {
        bag_id: '',
        merchant_id: null,
        merchant_business_id: null,
        date: {
          startDate: moment().startOf('day').toDate(),
          endDate: moment().endOf('day').toDate(),
        },
      },
      reportData: [],
      meta: {},
      page: 1,
      perPage: 20,
      pageOptions: [10, 20, 50, 100, 200, 500],
      fields: [
        { key: 'bag_id', label: 'BAG ID' },
        { key: 'custom_id', label: 'CUSTOM ID' },
        { key: 'is_closed', label: 'BAG STATUS' },
        { key: 'merchant_name', label: 'MERCHANT' },
        { key: 'merchant_business_name', label: 'MERCHANT BUSINESS' },
        { key: 'date', label: 'CREATED AT' },
        {
          key: 'no_of_orders', label: 'NO. OF ORDERS', class: 'text-right', thClass: 'text-right',
        },
        {
          key: 'actions', label: 'ACTIONS', class: 'text-center', thClass: 'text-center',
        },
      ],
      bagOrdersFields: [
        { key: 'order.waybill_number', label: 'WAYBILL NUMBER' },
        { key: 'order.order_no', label: 'ORDER NO' },
        { key: 'order.merchant.name', label: 'MERCHANT' },
        { key: 'order.customer_name', label: 'CUSTOMER' },
        { key: 'order.order_current_status.key', label: 'STATUS' },
        { key: 'scanned_by.first_name', label: 'SCANNED BY' },
      ],
      json_fields: {
        'BAG ID': 'bag_id',
        'CUSTOM ID': 'custom_id',
        MERCHANT: 'merchant_name',
        'MERCHANT BUSINESS': 'merchant_business_name',
        'CREATED AT': {
          field: 'date',
          // eslint-disable-next-line no-underscore-dangle
          callback: value => this.__dateTimeFormatter(value),
        },
        'BAG STATUS': {
          field: 'is_closed',
          callback: value => (value ? 'Closed' : 'Open'),
        },
        'NO. OF ORDERS': 'no_of_orders',
      },
    }
  },
  computed: {
    appliedFilterCount() {
      let count = 0
      if (this.filters.bag_id && this.filters.bag_id.trim()) count += 1
      if (this.filters.merchant_id) count += 1
      if (this.filters.merchant_business_id) count += 1
      if (this.filters.date && this.filters.date.startDate) count += 1
      return count
    },
  },
  watch: {
    page() {
      this.fetchData()
    },
    perPage() {
      this.page = 1
      this.fetchData()
    },
  },
  async mounted() {
    await this.fetchMerchants()
    this.fetchData()
  },
  methods: {
    avatarText,
    async fetchMerchants() {
      try {
        const { data } = await merchantRepository.getMerchantListForDropdown()
        this.merchantOptions = data.data.map(m => ({
          value: m.id,
          text: m.name,
        }))
      } catch (e) {
        // Ignore or handle
      }
    },
    async getMerchantBusinessList() {
      try {
        if (!this.filters.merchant_id) {
          this.merchantBusinessOptions = []
          this.filters.merchant_business_id = null
          return
        }
        const { data } = (await merchantRepository.getMerchantBusinessListDropdown(this.filters.merchant_id)).data
        this.merchantBusinessOptions = data
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },
    handleMerchantChange() {
      this.filters.merchant_business_id = null
      this.getMerchantBusinessList()
    },
    updateDate(val) {
      this.filters.date = val
    },
    applyFilters() {
      this.page = 1
      this.fetchData()
    },
    clearFilters() {
      this.filters.bag_id = ''
      this.filters.merchant_id = null
      this.filters.merchant_business_id = null
      this.merchantBusinessOptions = []
      this.filters.date = {
        startDate: null,
        endDate: null,
      }
      this.page = 1
      this.fetchData()
    },
    async fetchData() {
      this.loading = true
      try {
        let filterQuery = ''

        if (this.filters.bag_id && this.filters.bag_id.trim()) {
          filterQuery += `&bag_id=${encodeURIComponent(this.filters.bag_id.trim())}`
        }
        if (this.filters.merchant_id) {
          filterQuery += `&merchant_id=${this.filters.merchant_id}`
        }
        if (this.filters.merchant_business_id) {
          filterQuery += `&merchant_business_id=${this.filters.merchant_business_id}`
        }

        if (this.filters.date && this.filters.date.startDate && this.filters.date.endDate) {
          const startDate = moment(this.filters.date.startDate).format('YYYY-MM-DD')
          const endDate = moment(this.filters.date.endDate).format('YYYY-MM-DD')
          if (startDate === endDate) {
            filterQuery += `&date=${startDate}`
          } else {
            filterQuery += `&date=${startDate} to ${endDate}`
          }
        } else {
          filterQuery += '&date='
        }

        const { data } = await sortingRepository.getReturnBagReport(this.page, this.perPage, filterQuery)

        if (data && data.data) {
          this.reportData = data.data || []
          this.meta = {
            from: data.from,
            to: data.to,
            total: data.total,
          }
        } else {
          this.reportData = []
          this.meta = {}
        }
      } catch (e) {
        this.convertAndNotifyError(e)
      } finally {
        this.loading = false
      }
    },
    async getReturnBagReportNoPagination() {
      try {
        let filterQuery = ''

        if (this.filters.bag_id && this.filters.bag_id.trim()) {
          filterQuery += `&bag_id=${encodeURIComponent(this.filters.bag_id.trim())}`
        }
        if (this.filters.merchant_id) {
          filterQuery += `&merchant_id=${this.filters.merchant_id}`
        }
        if (this.filters.merchant_business_id) {
          filterQuery += `&merchant_business_id=${this.filters.merchant_business_id}`
        }

        if (this.filters.date && this.filters.date.startDate && this.filters.date.endDate) {
          const startDate = moment(this.filters.date.startDate).format('YYYY-MM-DD')
          const endDate = moment(this.filters.date.endDate).format('YYYY-MM-DD')
          if (startDate === endDate) {
            filterQuery += `&date=${startDate}`
          } else {
            filterQuery += `&date=${startDate} to ${endDate}`
          }
        } else {
          filterQuery += '&date='
        }

        const { data } = await sortingRepository.getReturnBagReportNoPagination(filterQuery)

        if (data && data.data && Array.isArray(data.data)) {
          return data.data
        } if (data && Array.isArray(data)) {
          return data
        }
        return []
      } catch (e) {
        this.convertAndNotifyError(e)
        return []
      }
    },
    viewOrders(item) {
      this.$router.push({
        name: 'return-bag-orders',
        params: { id: item.id },
        query: { bag_id: item.bag_id },
      })
    },
    async downloadCsv(bagId, customId) {
      try {
        const { data } = await sortingRepository.getReturnBagOrdersNoPagination(bagId)
        const bagOrders = data.data || []

        if (!bagOrders.length) {
          this.showErrorMessage('No orders found to download.')
          return
        }

        const headers = ['Waybill Number', 'Merchant Business', 'Customer Name', 'Customer Contact', 'COD', 'Collected COD', 'Return Charge', 'Weight', 'Status']
        const csvContent = [
          headers.join(','),
          ...bagOrders.map(item => {
            const waybill = item.order?.waybill_number || ''
            const merchantBusiness = item.order?.orderable?.business_name || ''
            const customerName = item.order?.customer_name || ''
            const customerContact = item.order?.customer_phone || ''
            const cod = item.order?.cod || 0
            const collectedCod = item.order?.collected_cod || 0
            const returnCharge = item.order?.return_charge || 0
            const weight = item.order?.weight || 0
            const status = item.order?.order_current_status?.name || ''

            const escapeField = str => `"${String(str).replace(/"/g, '""')}"`

            return [
              waybill,
              merchantBusiness,
              customerName,
              customerContact,
              cod,
              collectedCod,
              returnCharge,
              weight,
              status,
            ].map(escapeField).join(',')
          }),
        ].join('\n')

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', `Bag_${customId || bagId}_Orders.csv`)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (error) {
        this.convertAndNotifyError(error)
      }
    },

    // Generates QR Code data URL asynchronously
    async generateQrDataUrl(text) {
      if (!text) return null
      try {
        return await QRCode.toDataURL(String(text), {
          margin: 1,
          errorCorrectionLevel: 'M',
        })
      } catch (e) {
        return null
      }
    },

    // Generates Barcode data URL synchronously
    generateBarcodeDataUrl(text) {
      if (!text) return null

      const tempId = `barcode_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`
      const canvas = document.createElement('canvas')
      canvas.id = tempId
      canvas.style.position = 'fixed'
      canvas.style.left = '-9999px'
      canvas.style.top = '-9999px'
      document.body.appendChild(canvas)

      try {
        barcode(tempId, String(text))
        return canvas.toDataURL('image/png')
      } catch (e) {
        return null
      } finally {
        if (document.body.contains(canvas)) {
          document.body.removeChild(canvas)
        }
      }
    },

    async downloadPdf(item) {
      try {
        const { data } = await sortingRepository.getReturnBagOrdersNoPagination(item.id)
        const bagOrders = data.data || []

        if (!bagOrders.length) {
          this.showErrorMessage('No orders found to download.')
          return
        }

        // 1. Company details
        const courier = JSON.parse(localStorage.getItem('businessSettings')) || {}

        // 2. Generate QR Code & Barcode concurrently using CUSTOM ID
        const customIdentifier = item.custom_id ? String(item.custom_id) : ''
        const [qrDataUrl, barcodeDataUrl] = await Promise.all([
          this.generateQrDataUrl(customIdentifier),
          this.generateBarcodeDataUrl(customIdentifier),
        ])

        // 3. Initialize jsPDF in PORTRAIT format
        const doc = new JsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4',
        })
        const pageWidth = doc.internal.pageSize.getWidth()

        // ----------------- HEADER SECTION -----------------

        // Company Details (Left Aligned)
        doc.setFontSize(13)
        doc.setFont(undefined, 'bold')
        doc.setTextColor(0, 0, 0)
        doc.text(courier.company_name || '', 14, 15)

        doc.setFontSize(8)
        doc.setFont(undefined, 'normal')
        doc.setTextColor(80, 80, 80)
        if (courier.company_address) {
          doc.text(courier.company_address, 14, 21)
        }
        if (courier.company_phone) {
          doc.text(`Contact: ${courier.company_phone}`, 14, 27)
        }

        // QR Code & Label (Right Aligned - generated using custom_id)
        const qrSize = 22
        const qrX = pageWidth - 14 - qrSize
        const qrY = 9

        if (qrDataUrl) {
          try {
            doc.addImage(qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize)
            doc.setFontSize(7)
            doc.setFont(undefined, 'bold')
            doc.setTextColor(60, 60, 60)
            doc.text('SCAN CODE', qrX + (qrSize / 2), qrY + qrSize + 4, { align: 'center' })
          } catch (e) {
            // Ignore if QR fails
          }
        }

        // First Divider Line
        doc.setDrawColor(0, 0, 0)
        doc.setLineWidth(0.8)
        doc.line(14, 38, pageWidth - 14, 38)

        // ----------------- MERCHANT BUSINESS & BAG ID SECTION -----------------
        // Left Column: Merchant Business
        doc.setFontSize(7.5)
        doc.setFont(undefined, 'bold')
        doc.setTextColor(100, 100, 100)
        doc.text('MERCHANT BUSINESS', 14, 44)

        doc.setFontSize(10.5)
        doc.setFont(undefined, 'bold')
        doc.setTextColor(0, 0, 0)
        doc.text(item.merchant_business_name || item.merchant_name || '---', 14, 50)

        // Right Column: Bag ID
        doc.setFontSize(7.5)
        doc.setFont(undefined, 'bold')
        doc.setTextColor(100, 100, 100)
        doc.text('BAG ID', pageWidth - 14, 44, { align: 'right' })

        doc.setFontSize(10.5)
        doc.setFont(undefined, 'bold')
        doc.setTextColor(0, 0, 0)
        doc.text(String(item.bag_id || '---'), pageWidth - 14, 50, { align: 'right' })

        // Second Divider Line
        doc.setDrawColor(0, 0, 0)
        doc.setLineWidth(0.8)
        doc.line(14, 54, pageWidth - 14, 54)

        // ----------------- CENTERED BARCODE SECTION -----------------
        let tableStartY = 58
        if (barcodeDataUrl) {
          const barcodeWidth = 72
          const barcodeHeight = 13
          const barcodeX = (pageWidth - barcodeWidth) / 2
          const barcodeY = 61

          // Title
          doc.setFontSize(6.5)
          doc.setFont(undefined, 'bold')
          doc.setTextColor(120, 120, 120)
          doc.text('TRACKING BARCODE', pageWidth / 2, 59, { align: 'center' })

          // Barcode image
          try {
            doc.addImage(barcodeDataUrl, 'PNG', barcodeX, barcodeY, barcodeWidth, barcodeHeight)
            tableStartY = 78
          } catch (e) {
            tableStartY = 58
          }
        }

        // Thin Divider Line below barcode
        doc.setDrawColor(220, 220, 220)
        doc.setLineWidth(0.4)
        doc.line(14, tableStartY - 3, pageWidth - 14, tableStartY - 3)

        // ----------------- ORDERS TABLE -----------------
        const headers = [
          [
            'Waybill Number',
            'Merchant Business',
            'Customer Name',
            'Customer Contact',
            'COD',
            'Collected COD',
            'Return Charge',
            'Weight',
            'Status',
          ],
        ]

        const rows = bagOrders.map(bagOrder => [
          bagOrder.order?.waybill_number || '',
          bagOrder.order?.orderable?.business_name || '',
          bagOrder.order?.customer_name || '',
          bagOrder.order?.customer_phone || '',
          bagOrder.order?.cod || 0,
          bagOrder.order?.collected_cod || 0,
          bagOrder.order?.return_charge || 0,
          bagOrder.order?.weight || 0,
          bagOrder.order?.order_current_status?.name || '',
        ])

        doc.autoTable({
          head: headers,
          body: rows,
          startY: tableStartY,
          theme: 'striped',
          styles: {
            fontSize: 7,
            cellPadding: 2,
            overflow: 'linebreak',
          },
          headStyles: {
            fillColor: [115, 103, 240],
            fontSize: 7,
          },
          margin: { left: 14, right: 14 },
        })

        doc.save(`Bag_${item.custom_id || item.bag_id || item.id}_Orders.pdf`)
      } catch (error) {
        this.convertAndNotifyError(error)
      }
    },
  },
}
</script>

<style lang="scss">
@import '~@core/scss/vue/libs/vue-select.scss';

.width-100 {
  width: 100px;
}

.bag-status-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 0.5rem;
  flex-shrink: 0;

  &--open {
    background-color: #28c76f;
  }

  &--closed {
    background-color: #ea5455;
  }
}
</style>
